/**
 * Kochere Variant Gallery Filter
 *
 * Filters the product gallery to show only images matching the selected variant.
 *
 * Tagging convention (Shopify Admin → image alt text):
 *   Both options:       "Bag front [12oz] [drip grind]"  → only 12oz Drip Grind
 *   Size only:          "Bag detail [12oz]"               → all 12oz variants
 *   Grind only:         "Grind chart [drip grind]"        → all Drip Grind variants
 *   Shared/universal:   "Farm photo [all]"  OR  no tags   → always visible
 *   Variant featured:   auto-detected — no tag needed
 *
 * Rule: EVERY tag on an image must match one of the variant's options.
 *       Fewer tags = broader visibility. No tags = always shown.
 */
(function () {
  'use strict';

  /* ── Helpers ── */

  /**
   * Extract ALL bracket tags from alt text.
   * e.g. "Coffee bag [12oz] [drip grind]" → ["12oz", "drip grind"]
   * Returns empty array if no bracket tags found.
   */
  function getTags(alt) {
    if (!alt) return [];
    var matches = alt.match(/\[([^\]]+)\]/g);
    if (!matches) return [];
    return matches.map(function (m) {
      return m.slice(1, -1).trim().toLowerCase();
    });
  }

  /**
   * Determine if a slide should be visible for the given variant.
   * @param {string[]}   tags        - bracket tags extracted from alt text
   * @param {string[]}   options     - active variant's option values (all lowercase)
   * @param {string}     mediaId     - the slide's data-media-id
   * @param {number|null} featuredId - the variant's featured_media_id
   */
  function shouldShow(tags, options, mediaId, featuredId) {
    // Variant's own featured image — always show
    if (featuredId && String(mediaId) === String(featuredId)) return true;
    // No tags at all — shared image, always show
    if (tags.length === 0) return true;
    // Has [all] tag — always show
    if (tags.indexOf('all') !== -1) return true;
    // Every tag must match one of the variant's options
    for (var i = 0; i < tags.length; i++) {
      if (options.indexOf(tags[i]) === -1) return false;
    }
    return true;
  }

  /* ── Main initializer ── */

  function initFilter() {
    var jsonEl = document.querySelector('[data-koc-variant-options]');
    if (!jsonEl) return;

    var config;
    try {
      config = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    var variants = config.variants || [];

    function findVariant(variantId) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === variantId) return variants[i];
      }
      return null;
    }

    // ── Capture original slides ──
    var mainList = document.querySelector('[data-koc-main-list]');
    var thumbList = document.querySelector('[data-koc-thumb-list]');
    var stackedList = document.querySelector('[data-koc-gallery-stacked] [data-gal-list]');

    if (!mainList) return;

    var allMainSlides = Array.from(mainList.children).map(function (el) {
      return el.cloneNode(true);
    });
    var allThumbSlides = thumbList
      ? Array.from(thumbList.children).map(function (el) { return el.cloneNode(true); })
      : [];
    var allStackedItems = stackedList
      ? Array.from(stackedList.children).map(function (el) { return el.cloneNode(true); })
      : [];

    var sliderEl = document.querySelector('[data-koc-gallery-slider] splide-slider');

    function destroySplide() {
      if (!sliderEl) return false;
      var mainSplide = sliderEl.slider;
      if (mainSplide && typeof mainSplide.destroy === 'function') {
        try { mainSplide.destroy(); } catch (e) { /* already destroyed */ }
        sliderEl.slider = null;
      }
      var thumbSplide = sliderEl.querySelector('[data-switcher-thumbs]');
      if (thumbSplide && thumbSplide.splide && typeof thumbSplide.splide.destroy === 'function') {
        try { thumbSplide.splide.destroy(); } catch (e) { /* ok */ }
      }
      return true;
    }

    function reinitSplide() {
      if (!sliderEl) return;
      requestAnimationFrame(function () {
        if (typeof sliderEl.initialize === 'function') {
          sliderEl.initialize();
        }
      });
    }

    /**
     * Test a slide element against the current variant.
     */
    function slideMatches(slide, options, featuredId) {
      var alt = slide.getAttribute('data-variant-alt') || '';
      var tags = getTags(alt);
      var mediaId = slide.getAttribute('data-media-id');
      return shouldShow(tags, options, mediaId, featuredId);
    }

    /**
     * Filter and rebuild the gallery for a given variant.
     */
    function filterGallery(variantId) {
      var variant = findVariant(variantId);
      if (!variant) return;

      var options = variant.options; // e.g. ["12oz", "drip grind"]
      var featuredId = variant.featured_media_id;

      // ── Filter slides ──
      var visibleMain = allMainSlides.filter(function (s) {
        return slideMatches(s, options, featuredId);
      });
      var visibleThumbs = allThumbSlides.filter(function (s) {
        return slideMatches(s, options, featuredId);
      });

      // ── Reorder: variant featured image first ──
      if (featuredId) {
        visibleMain = reorderFeaturedFirst(visibleMain, featuredId);
        visibleThumbs = reorderFeaturedFirst(visibleThumbs, featuredId);
      }

      // ── Destroy existing Splide ──
      destroySplide();

      // ── Rebuild main slider ──
      if (mainList) {
        mainList.innerHTML = '';
        visibleMain.forEach(function (slide) {
          mainList.appendChild(slide.cloneNode(true));
        });
        mainList.classList.add('splide-slider--h-scroll');
      }

      // ── Rebuild thumbnails ──
      if (thumbList) {
        thumbList.innerHTML = '';
        visibleThumbs.forEach(function (slide) {
          thumbList.appendChild(slide.cloneNode(true));
        });
        thumbList.classList.add('splide-slider--h-scroll');
      }

      // ── Filter stacked layout ──
      if (stackedList) {
        var visibleStacked = allStackedItems.filter(function (item) {
          return slideMatches(item, options, featuredId);
        });
        if (featuredId) {
          visibleStacked = reorderFeaturedFirst(visibleStacked, featuredId);
        }
        stackedList.innerHTML = '';
        visibleStacked.forEach(function (item, i) {
          var node = item.cloneNode(true);
          node.classList.remove('stacked-image-full', 'stacked-image-half');
          node.classList.add(i === 0 ? 'stacked-image-full' : 'stacked-image-half');
          stackedList.appendChild(node);
        });
      }


      // ── Reinitialize Splide ──
      reinitSplide();
    }

    function reorderFeaturedFirst(slides, featuredId) {
      var idx = -1;
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id')) === String(featuredId)) {
          idx = i;
          break;
        }
      }
      if (idx > 0) {
        var item = slides.splice(idx, 1)[0];
        slides.unshift(item);
      }
      return slides;
    }

    // ── Listen for variant changes ──
    document.addEventListener('variant:change', function (e) {
      var variantId = e.detail && e.detail.variantId;
      if (variantId) filterGallery(variantId);
    });

    // ── Initial filter on page load ──
    var initialVariantId = null;
    var urlParams = new URLSearchParams(window.location.search);
    var urlVariant = urlParams.get('variant') || urlParams.get('variant-id');
    if (urlVariant) {
      initialVariantId = parseInt(urlVariant, 10);
    }
    if (!initialVariantId && variants.length > 0) {
      initialVariantId = variants[0].id;
    }
    if (initialVariantId) {
      filterGallery(initialVariantId);
    }
  }

  // ── Boot ──
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilter);
  } else {
    initFilter();
  }
})();
